import { useEffect, useState } from 'react'
import { collection, query, where, getCountFromServer } from 'firebase/firestore'
import { db } from '@fleetops/firebase'
import { firestorePaths } from '@fleetops/types'
import { useSession } from '@/store/auth.store'
import { fleetQueryChunks, fleetScopeKey } from '@/lib/fleetScope'

// Just the number, for the sidebar badge. AppLayout renders on every page, so
// it used to hold a realtime listener over the whole trailers collection —
// every trailer document in memory, and a re-render of the entire layout on
// any change — to display one integer. A server-side count is a single read
// (billed per 1000 documents) and needs no snapshot.
export function useTrailersCount(): number | undefined {
  const session = useSession()
  const [count, setCount] = useState<number | undefined>(undefined)

  // Mirrors useTrailers(): a fleet-scoped user counts only what they can read,
  // or the count query itself would be denied by the rules. Chunked for the
  // same reason — a count that quietly ignored fleets past the `in` limit
  // would disagree with the list it labels.
  const scopeKey = fleetScopeKey(session)

  useEffect(() => {
    if (!session) return
    let cancelled = false

    const counts = fleetQueryChunks(session).map((chunk) =>
      getCountFromServer(query(
        collection(db, firestorePaths.trailers(session.tenantId)),
        where('tenantId', '==', session.tenantId),
        ...(chunk ? [where('fleetId', 'in', chunk)] : []),
      )).then((snap) => snap.data().count))

    // Chunks are disjoint by construction (a trailer has one fleetId), so the
    // totals add up without deduplication.
    Promise.all(counts)
      .then((all) => { if (!cancelled) setCount(all.reduce((s, n) => s + n, 0)) })
      .catch(() => { if (!cancelled) setCount(undefined) })

    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.tenantId, session?.role, scopeKey])

  return count
}
